import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import type { ArchivedUsersDto, UserDto } from '@/services/api/tenant/user'
import { ManagerDetails, ParticipantsState } from './types'

const initialState: ParticipantsState = {
  participantViews: {
    openImportParticipantsMenu: false,
    openAddManuallyParticipantsMenu: false,
    openCsvImportErrors: false,
    openEmptyParticipantListMode: false,
  },
  openCreateManagerForm: false,
  openCreateManagerDetailsForm: false,
  newManagerUser: null,
  editManagerUser: null,
  selectedParticipant: null,
  selectedParticipants: [],
  selectedArchivedParticipants: [],
  archivePaginationDisabled: null,
  triggerAllArchivedUsersFetch: false,
  allSelectedStatus: {
    isChecked: false,
    isIndeterminate: false,
  },
  allArchivedSelectedStatus: {
    isChecked: false,
    isIndeterminate: false,
  },
  dialogVisibility: {
    isArchiveDialogOpen: false,
    isUnarchiveDialogOpen: false,
    selectedUser: null,
    selectedUsers: null,
  },
  openDownloadTrainingRecordsDialog: false,
}

export const participantsSlice = createSlice({
  name: 'participantsSlice',
  initialState,
  reducers: {
    openImportParticipantsMenu(state, action) {
      state.participantViews.openImportParticipantsMenu = action.payload
    },
    openAddManuallyParticipantsMenu(state, action) {
      state.participantViews.openAddManuallyParticipantsMenu = action.payload
    },
    openCsvImportErrors(state, action) {
      state.participantViews.openCsvImportErrors = action.payload
    },
    openEmptyParticipantListMode(state, action) {
      state.participantViews.openEmptyParticipantListMode = action.payload
    },
    setOpenCreateManagerForm(state, action) {
      state.openCreateManagerForm = action.payload
    },
    setOpenCreateManagerDetailsForm(state, action: PayloadAction<boolean>) {
      state.openCreateManagerDetailsForm = action.payload
    },
    setNewManagerUser(state, action) {
      state.newManagerUser = action.payload
    },
    setEditManagerUser(state, action: PayloadAction<ManagerDetails | null>) {
      state.editManagerUser = action.payload
    },
    setSelectedParticipant(state, action) {
      state.selectedParticipant = action.payload
    },
    setSelectedParticipants(state, action) {
      state.selectedParticipants = action.payload
    },
    setSelectedArchivedParticipants(state, action) {
      state.selectedArchivedParticipants = action.payload
    },
    setArchivePaginationDisabled(state, action) {
      state.archivePaginationDisabled = action.payload
    },
    setAllSelectedStatus(state, action) {
      state.allSelectedStatus.isChecked = action.payload.isChecked
      state.allSelectedStatus.isIndeterminate = action.payload.isIndeterminate
    },
    setAllArchivedSelectedStatus(state, action) {
      state.allArchivedSelectedStatus.isChecked = action.payload.isChecked
      state.allArchivedSelectedStatus.isIndeterminate = action.payload.isIndeterminate
    },
    setTriggerAllArchivedUsersFetch(state, action) {
      state.triggerAllArchivedUsersFetch = action.payload
    },
    openArchiveDialog(state, action: { payload: { user?: UserDto | null; users?: string[] | null } }) {
      state.dialogVisibility.isArchiveDialogOpen = true

      if ('user' in action.payload) {
        state.dialogVisibility.selectedUser = action.payload.user ?? null
        state.dialogVisibility.selectedUsers = null
      } else if ('users' in action.payload) {
        state.dialogVisibility.selectedUsers = action.payload.users ?? null
        state.dialogVisibility.selectedUser = null
      }
    },
    closeArchiveDialog(state) {
      state.dialogVisibility.isArchiveDialogOpen = false
      state.dialogVisibility.selectedUser = null
      state.dialogVisibility.selectedUsers = null
    },
    openUnarchiveDialog(
      state,
      action: { payload: { user?: UserDto | ArchivedUsersDto | null; users?: string[] | null } },
    ) {
      state.dialogVisibility.isUnarchiveDialogOpen = true

      if ('user' in action.payload) {
        state.dialogVisibility.selectedUser = action.payload.user ?? null
        state.dialogVisibility.selectedUsers = null
      } else if ('users' in action.payload) {
        state.dialogVisibility.selectedUsers = action.payload.users ?? null
        state.dialogVisibility.selectedUser = null
      }
    },
    closeUnarchiveDialog(state) {
      state.dialogVisibility.isUnarchiveDialogOpen = false
      state.dialogVisibility.selectedUser = null
    },
    openDownloadTrainingRecordsDialog(state, action) {
      state.openDownloadTrainingRecordsDialog = action.payload
    },
  },
})

export const participantReducer = persistReducer(
  {
    key: 'rtk:participant',
    storage,
    whitelist: [''],
  },
  participantsSlice.reducer,
)
